import { MouseEvent, ReactElement, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LocalStorage from 'utils/bom-dom-manipulation/local-storage';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { NewCasinoGame } from 'interfaces/new-casino';
import { Viewport } from 'components/Viewport';
import { CasinoGameCardSkeleton } from 'newcomponents/Shared/casino/CasinoGameCard/Skeleton';
import { useOpenLoginPopup } from 'hooks/account/useOpenLoginPopup';
import { toggleFavorite } from 'store/actions';
import { getFavoriteActiveStateById } from 'store/selectors/fav-data';
import { getIsLoggedIn } from 'store/selectors/user-data';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

type Props = {
  game: NewCasinoGame;
  casinoPlayRealBtnSize?: string;
  casinoNameSize?: string;
  fromAccount?: boolean;
  index?: number;
  shouldStoreOffset?: boolean;
  scaleOut?: boolean;
  setVisible?: (arg: boolean) => void;
  hidePartials?: {
    name?: boolean;
    providerTitle?: boolean;
    favorite?: boolean;
  };
};

const { CasinoGameCardMobile } = lazy(
  () => import(/* webpackChunkName: "casino-game-card-mobile" */ './Mobile')
);

const { CasinoGameCardDesktop } = lazy(
  () => import(/* webpackChunkName: "casino-game-card-desktop" */ './Desktop')
);

export const CasinoGameCard = (props: Props): ReactElement => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const openLoginPopup = useOpenLoginPopup();
  const isFavorite = useSelector(
    getFavoriteActiveStateById(props.game.extearnal_game_id, 'casino')
  );

  const onFavoriteToggle = useCallback(
    (e: MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();

      if (!isLoggedIn && SpringConfigs.USER_PERSONALIZATION_ENABLED) {
        LocalStorage.setItem(
          storageKeyName('account', 'UNSIGNED_FAV_CASINO_GAME'),
          JSON.stringify(props.game)
        );
        openLoginPopup();
      } else {
        dispatch(toggleFavorite({ entity: props.game, groupKey: 'casino' }));
      }
    },
    [props.game, isLoggedIn]
  );

  return (
    <Viewport
      desktop={CasinoGameCardDesktop}
      mobile={CasinoGameCardMobile}
      innerProps={{
        ...props,
        isFavorite,
        onFavoriteToggle,
        casinoNameSize: props.casinoNameSize,
        casinoPlayRealBtnSize: props.casinoPlayRealBtnSize,
        setVisible: props.setVisible
      }}
      fallback={<CasinoGameCardSkeleton hidePartials={props.hidePartials} />}
    />
  );
};
